import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
  pdf,
} from "@react-pdf/renderer";

import React, { useState, useEffect } from "react";
import axios from "axios";
import BASE_URL from "../../../assets/global/url";
import {
  formatToTwoDecimal,
  getCurrencySymbol,
} from "../../../utils/numberFormatter";
import NunitoSemiBold from "../../../assets/fonts/Nunito/Nunito-SemiBold.ttf";
import PDFPreviewFrame from "../../../components/PDFPreviewFrame";

Font.register({
  family: "Nunito",
  src: NunitoSemiBold,
});

const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    backgroundColor: "#FFFFFF",
    padding: 30,
  },
  header: {
    fontSize: 20,
    marginBottom: 20,
    textAlign: "center",
  },
  currencySymbol: {
    fontSize: 10,
    fontFamily: "Nunito",
  },
  table: {
    display: "table",
    width: "100%",
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderRightWidth: 0,
    borderBottomWidth: 0,
  },
  tableRow: {
    margin: "auto",
    flexDirection: "row",
  },
  tableCol: {
    width: "8.33333%",
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    padding: 2,
  },
  tableCell: {
    margin: "auto",
    marginTop: 5,
    fontSize: 7,
    textAlign: "left",
  },
  totalTableCol: {
    padding: 5,
    width: "58.33331%",
  },
  totalTableCell: {
    paddingTop: 5,
    paddingBottom: 5,
    fontSize: 10,
    paddingLeft: 10,
  },
  infoSection: {
    marginBottom: 20,
  },
  row: {
    flexDirection: "row",
    marginBottom: 10,
  },
  column: {
    marginRight: 20,
  },
  label: {
    fontSize: 10,
    color: "#666666",
    marginBottom: 4,
  },
  value: {
    fontSize: 10,
    padding: 8,
    borderBottom: "1px solid black",
  },
  summaryColumn: {
    flexDirection: "row",
    justifyContent: "flex-end",
  },
  summaryValue: {
    fontSize: 10,
    width: 85,
    textAlign: "center",
    borderBottom: "1px solid black",
  },
  totalCard: {
    width: "11rem",
    padding: "10px",
    gap: "0.5rem",
  },
  totalContainer: {
    marginTop: "20px",
    flexDirection: "row",
  },
  totalCardSecond: {
    width: "45%",
    padding: "10px",
  },
  totalCardSecondBetween: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  totalCardSecondBetweenTotalAmount: {
    backgroundColor: "#0A9CBC",
    borderRadius: "5px",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    padding: 5,
    paddingLeft: 8,
    paddingRight: 8,
    marginTop: "5px",
  },
  labelSpecial: {
    fontSize: 10,
    color: "#fff",
    marginBottom: 4,
    fontWeight: 600,
  },
  tableHead: {
    backgroundColor: "#E9ECEF",
    padding: 5,
  },
  footer: {
    position: "absolute",
    top: 8,
    left: 0,
    right: 0,
    flexDirection: "row",
    justifyContent: "space-between",
    padding: 20,
  },
  footerText: {
    fontSize: 8,
    color: "#000",
  },
});

// Fetches the invoice and renders the PDF to a blob URL exactly once per open.
// PDFViewer / PDFDownloadLink re-render the whole PDF (and reload the iframe)
// on every parent re-render, which makes the preview blink, so the caller
// shows the returned url in a plain <iframe> and <a download> instead.
// getSpecificInvoice already includes the customer and currency, so the full
// customer, currency, product and cutoff lists are not needed here.
export const useInvoicePDF = (id, enabled = true) => {
  const [state, setState] = useState({
    url: null,
    transactionId: "",
    loading: false,
    error: null,
  });

  useEffect(() => {
    if (!enabled || !id) return;
    let cancelled = false;
    let createdUrl = null;

    setState({ url: null, transactionId: "", loading: true, error: null });

    const load = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/invoice/getSpecificInvoice`, {
          params: { id },
        });
        if (!res.data) return;

        const { data: SalesInvoiceData, result: ProductTagInvoice } = res.data;

        let originalTotal = 0;
        if (
          SalesInvoiceData.isReOpen &&
          SalesInvoiceData.reopen_sales_invoice_id
        ) {
          try {
            const origRes = await axios.get(
              `${BASE_URL}/invoice/fetchInvoiceById/${SalesInvoiceData.reopen_sales_invoice_id}`,
            );
            originalTotal = origRes.data.invoice?.total_amount ?? 0;
          } catch {
            originalTotal = 0;
          }
        }

        const items = ProductTagInvoice.map((item) => ({
          productCode: item.product_code,
          productName: item.product_name,
          unitPrice: item.unit_price,
          quantity: item.quantity,
          moisture: item.moisture,
          netWeight: item.net_weight,
          staticNetWeight: item.static_net_weight,
          subtotal: item.subtotal,
          unitOfMeasure: item.unit_of_measure,
        }));

        const subTotal = items.reduce(
          (acc, item) => acc + (item.subtotal || 0),
          0,
        );
        const discountValue =
          parseFloat(SalesInvoiceData.transaction_discount) || 0;
        const discountType = SalesInvoiceData.discount_type || "fixed";

        let preVatBase = subTotal;
        if (discountValue > 0) {
          preVatBase -=
            discountType === "percentage"
              ? (discountValue / 100) * subTotal
              : discountValue;
        }

        let totalQuantity = 0;
        let totalNetWeight = 0;
        items.forEach((item) => {
          totalQuantity +=
            parseFloat(String(item.quantity).replace(/,/g, "")) || 0;
          totalNetWeight +=
            parseFloat(String(item.netWeight).replace(/,/g, "")) || 0;
        });

        const customer = SalesInvoiceData.customer;
        const customerName = customer?.company_name
          ? customer.company_name
          : customer
            ? `${customer.first_name} ${customer.last_name}`
            : "";

        const pdfData = {
          transactionId: SalesInvoiceData.transaction_id,
          customerName,
          currencySymbol: getCurrencySymbol(
            SalesInvoiceData.currency ? [SalesInvoiceData.currency] : [],
            SalesInvoiceData.currency_id,
          ),
          selectedMethod: SalesInvoiceData.payment_method,
          selectedDueDate: SalesInvoiceData.due_date,
          selectedInvoiceDate: SalesInvoiceData.invoice_date,
          inputPaymentTerms: SalesInvoiceData.payment_terms,
          selectedDestination: SalesInvoiceData.destination,
          totalAmount: SalesInvoiceData.total_amount,
          displayDiscount: SalesInvoiceData.transaction_discount,
          modalDiscountType: discountType,
          containerNumber: SalesInvoiceData.container_number,
          warehouseName: SalesInvoiceData.warehouse?.name,
          pier: SalesInvoiceData.pier,
          status: SalesInvoiceData.status,
          shipping_fee: SalesInvoiceData.shipping_fee,
          vatPercent: parseFloat(SalesInvoiceData.vat_percent) || 0,
          isReOpen: SalesInvoiceData.isReOpen ?? false,
          reOpenAmount: parseFloat(SalesInvoiceData.re_open_amount) || 0,
          originalTotal,
          taxRate: parseFloat(SalesInvoiceData.tax_rate) || 0,
          isSpecialTax: SalesInvoiceData.isSpecialTax ?? false,
          displayWeighingFee: parseFloat(SalesInvoiceData.weighing_fee) || 0,
          displayOtherFee: parseFloat(SalesInvoiceData.other_fee) || 0,
          preVatBase,
          subTotal,
          items,
          totalQuantity,
          totalNetWeight,
        };
        if (cancelled) return;

        const blob = await pdf(<InvoicePDF data={pdfData} />).toBlob();
        if (cancelled) return;

        createdUrl = URL.createObjectURL(blob);
        setState({
          url: createdUrl,
          transactionId: pdfData.transactionId,
          loading: false,
          error: null,
        });
      } catch (err) {
        console.log(err);
        if (!cancelled) {
          setState({ url: null, transactionId: "", loading: false, error: err });
        }
      }
    };

    load();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
      // Drop the revoked url so a reopened modal shows the spinner, not a
      // broken frame, until the new PDF is ready.
      setState({ url: null, transactionId: "", loading: false, error: null });
    };
  }, [id, enabled]);

  return state;
};

export const InvoicePDFFrame = (props) => (
  <PDFPreviewFrame title="Sales Invoice" {...props} />
);

// Pure document built from the data prepared in useInvoicePDF.
const InvoicePDF = ({ data }) => {
  const {
    transactionId,
    customerName,
    currencySymbol,
    selectedMethod,
    selectedDueDate,
    selectedInvoiceDate,
    inputPaymentTerms,
    selectedDestination,
    totalAmount,
    displayDiscount,
    modalDiscountType,
    containerNumber,
    warehouseName,
    pier,
    status,
    shipping_fee,
    vatPercent,
    isReOpen,
    reOpenAmount,
    originalTotal,
    taxRate,
    isSpecialTax,
    displayWeighingFee,
    displayOtherFee,
    preVatBase,
    subTotal,
    items,
    totalQuantity,
    totalNetWeight,
  } = data;

  const calculateReturnedQuantity = (
    staticNetWeight,
    currentQuantity,
    moisture,
  ) => {
    const moistureDecimal = moisture / 100;
    const originalWeight = staticNetWeight / (1 - moistureDecimal);
    const returnedQuantity = originalWeight - currentQuantity;
    return returnedQuantity;
  };

  const getTotalMoisture = (items) => {
    let totalMoisture = 0;
    items.forEach((item) => {
      const quantity = parseFloat(item.quantity) || 0;
      const moisture = parseFloat(item.moisture) || 0;
      totalMoisture += quantity * (moisture / 100);
    });
    return Math.round(totalMoisture);
  };

  const today = new Date().toLocaleDateString();

  // ── helpers ──────────────────────────────────────────────────────────────────
  // Format a withholding-tax label:
  //   • if percentage → "Withholding Tax (1%)"
  //   • if fixed peso  → "Withholding Tax:"
  const withholdingTaxLabel =
    taxRate > 0 ? `Withholding Tax (${taxRate}%): ` : "Withholding Tax: ";

  // Format a discount label:
  //   • if percentage  → "Discount (10%)"
  //   • if fixed peso  → "Discount:"
  const discountLabel =
    modalDiscountType === "percentage"
      ? `Discount (${displayDiscount}%): `
      : "Discount: ";

  // Computed discount amount in currency
  const discountAmountPDF =
    modalDiscountType === "percentage"
      ? (displayDiscount / 100) * subTotal
      : parseFloat(displayDiscount) || 0;

  // Withholding tax amount
  const withholdingTaxAmount = (taxRate / 100) * preVatBase;

  // ─────────────────────────────────────────────────────────────────────────────

  /** Renders the right-hand summary block (shared between regular & re-open) */
  const renderSummaryBlock = (isReOpenMode) => (
    <>
      {/* 1. Total Qty Amount */}
      <View style={styles.summaryColumn}>
        <Text style={[styles.label, { color: "#6c757d" }]}>
          Total Qty Amount:{" "}
        </Text>
        <Text style={{ ...styles.summaryValue, color: "#6c757d" }}>
          <Text style={styles.currencySymbol}>{currencySymbol} </Text>
          {formatToTwoDecimal(
            items.reduce(
              (acc, item) =>
                acc +
                (parseFloat(item.quantity) || 0) *
                  (parseFloat(item.unitPrice) || 0),
              0,
            ),
          )}
        </Text>
      </View>

      {/* 2. Total Amount of Moisture */}
      <View style={styles.summaryColumn}>
        <Text style={[styles.label, { color: "red" }]}>
          Total Amount of Moisture:{" "}
        </Text>
        <Text style={{ ...styles.summaryValue, color: "red" }}>
          <Text style={styles.currencySymbol}>{currencySymbol} </Text>
          {formatToTwoDecimal(
            items.reduce((acc, item) => {
              const qty = parseFloat(item.quantity) || 0;
              const moisture = parseFloat(item.moisture) || 0;
              const price = parseFloat(item.unitPrice) || 0;
              return acc + Math.round((qty * moisture) / 100) * price;
            }, 0),
          )}
        </Text>
      </View>

      {/* 3. Total Net Qty Amount */}
      <View style={styles.summaryColumn}>
        <Text style={[styles.label, { color: "red" }]}>
          Total Net Qty Amount:{" "}
        </Text>
        <Text style={{ ...styles.summaryValue, color: "red" }}>
          <Text style={styles.currencySymbol}>{currencySymbol} </Text>
          {formatToTwoDecimal(
            items.reduce(
              (acc, item) =>
                acc +
                (parseFloat(item.netWeight) || 0) *
                  (parseFloat(item.unitPrice) || 0),
              0,
            ),
          )}
        </Text>
      </View>

      {/* 4. Subtotal */}
      <View style={styles.summaryColumn}>
        <Text style={[styles.label, { color: "#6c757d" }]}>Subtotal: </Text>
        <Text style={{ ...styles.summaryValue, color: "#6c757d" }}>
          <Text style={styles.currencySymbol}>{currencySymbol} </Text>
          {formatToTwoDecimal(subTotal)}
        </Text>
      </View>

      {/* 5. Discount — only show if > 0 */}
      {displayDiscount > 0 && (
        <View style={styles.summaryColumn}>
          {/* Label: "Discount (10%):" for percentage, "Discount:" for fixed */}
          <Text style={[styles.label, { color: "red" }]}>{discountLabel}</Text>
          <Text style={{ ...styles.summaryValue, color: "red" }}>
            {modalDiscountType !== "percentage" && (
              <Text style={styles.currencySymbol}>{currencySymbol} </Text>
            )}
            {formatToTwoDecimal(discountAmountPDF)}
          </Text>
        </View>
      )}

      {/* 6. Gross Amount */}
      <View style={styles.summaryColumn}>
        <Text style={[styles.label, { color: "#6c757d" }]}>Gross Amount: </Text>
        <Text style={{ ...styles.summaryValue, color: "#6c757d" }}>
          <Text style={styles.currencySymbol}>{currencySymbol} </Text>
          {formatToTwoDecimal(preVatBase)}
        </Text>
      </View>

      {/* 7. VAT — always show */}
      <View style={styles.summaryColumn}>
        <Text
          style={[
            styles.label,
            { color: vatPercent > 0 ? "#2D6A4F" : "#6c757d" },
          ]}
        >
          {vatPercent > 0 ? `VAT (${vatPercent}%): ` : "Non-VAT: "}
        </Text>
        <Text
          style={{
            ...styles.summaryValue,
            color: vatPercent > 0 ? "#2D6A4F" : "#6c757d",
          }}
        >
          {vatPercent > 0
            ? formatToTwoDecimal((vatPercent / 100) * preVatBase)
            : "0.00"}
        </Text>
      </View>

      {/* 8. Withholding Tax — only show if taxRate > 0 */}
      {taxRate > 0 && (
        <View style={styles.summaryColumn}>
          <Text style={[styles.label, { color: "red" }]}>
            {withholdingTaxLabel}
          </Text>
          <Text style={{ ...styles.summaryValue, color: "red" }}>
            ({formatToTwoDecimal(withholdingTaxAmount)})
          </Text>
        </View>
      )}

      {/* 8b. Tax (Special Computation) — only show when isSpecialTax is true */}
      {isSpecialTax && (
        <View style={styles.summaryColumn}>
          <Text style={[styles.label, { color: "#2D6A4F" }]}>
            Tax (Special Computation):{" "}
          </Text>
          <Text style={{ ...styles.summaryValue, color: "#2D6A4F" }}>
            <Text style={styles.currencySymbol}>{currencySymbol} </Text>
            {formatToTwoDecimal(preVatBase * 0.1088)}
          </Text>
        </View>
      )}

      {/* 9. Weighing Fee (Non-Taxable) — only show if > 0 */}
      {displayWeighingFee > 0 && (
        <View style={styles.summaryColumn}>
          <Text style={[styles.label, { color: "#6c757d" }]}>
            Weighing Fee (Non-Taxable):{" "}
          </Text>
          <Text style={{ ...styles.summaryValue, color: "#6c757d" }}>
            <Text style={styles.currencySymbol}>{currencySymbol} </Text>
            {formatToTwoDecimal(displayWeighingFee)}
          </Text>
        </View>
      )}

      {/* 10. Other Fee (Non-Taxable) — only show if > 0 */}
      {displayOtherFee > 0 && (
        <View style={styles.summaryColumn}>
          <Text style={[styles.label, { color: "#6c757d" }]}>
            Other Fee (Non-Taxable):{" "}
          </Text>
          <Text style={{ ...styles.summaryValue, color: "#6c757d" }}>
            <Text style={styles.currencySymbol}>{currencySymbol} </Text>
            {formatToTwoDecimal(displayOtherFee)}
          </Text>
        </View>
      )}

      {/* Re-open specific rows */}
      {isReOpenMode && (
        <>
          <View
            style={{
              borderBottomWidth: 0.5,
              borderBottomColor: "#dee2e6",
              marginVertical: 4,
            }}
          />

          {/* Original Total Amount */}
          <View style={styles.summaryColumn}>
            <Text style={[styles.label, { color: "#6c757d" }]}>
              Original Total Amount:{" "}
            </Text>
            <Text style={{ ...styles.summaryValue, color: "#6c757d" }}>
              <Text style={styles.currencySymbol}>{currencySymbol} </Text>
              {formatToTwoDecimal(originalTotal)}
            </Text>
          </View>

          {/* Re-Open Amount */}
          <View style={styles.summaryColumn}>
            <Text
              style={[
                styles.label,
                { color: reOpenAmount < 0 ? "red" : "#2D6A4F" },
              ]}
            >
              Re-Open Amount:{" "}
            </Text>
            <Text
              style={{
                ...styles.summaryValue,
                color: reOpenAmount < 0 ? "red" : "#2D6A4F",
              }}
            >
              {reOpenAmount < 0 ? "-" : "+"}
              <Text style={styles.currencySymbol}>{currencySymbol} </Text>
              {formatToTwoDecimal(Math.abs(reOpenAmount))}
            </Text>
          </View>
        </>
      )}

      {/* 11. Total Amount */}
      <View style={styles.summaryColumn}>
        <Text style={[styles.label, { color: "red", fontWeight: 700 }]}>
          Total Amount:{" "}
        </Text>
        <Text style={{ ...styles.summaryValue, color: "red", fontWeight: 700 }}>
          <Text style={styles.currencySymbol}>{currencySymbol} </Text>
          {formatToTwoDecimal(totalAmount)}
        </Text>
      </View>
    </>
  );

  return (
    <Document title={`Sales Invoice - ${transactionId}`}>
      <Page size="A4" style={styles.page} orientation="portrait">
        <Text style={styles.header}>Sales Invoice</Text>

        <View style={styles.infoSection}>
          <View style={styles.row}>
            <View style={{ ...styles.column }}>
              <Text style={styles.label}>CUSTOMER: </Text>
              <Text style={styles.value}>{customerName || ""}</Text>
            </View>
            <View style={{ ...styles.column, flex: 1 }}>
              <Text style={styles.label}>Invoice Date: </Text>
              <Text style={styles.value}>{selectedInvoiceDate}</Text>
            </View>
            <View style={{ ...styles.column, flex: 1 }}>
              <Text style={styles.label}>Order Number: </Text>
              <Text style={styles.value}>{transactionId}</Text>
            </View>
          </View>
        </View>

        {/* ── ITEMS TABLE ── */}
        <View style={styles.table}>
          <View style={[styles.tableRow]}>
            <View
              style={[
                styles.tableCol,
                styles.tableHead,
                { borderLeftWidth: 1 },
              ]}
            >
              <Text
                style={[
                  styles.tableCell,
                  { fontWeight: 600, textAlign: "left" },
                ]}
              >
                Warehouse
              </Text>
            </View>

            <View style={[styles.tableCol, styles.tableHead]}>
              <Text
                style={[
                  styles.tableCell,
                  { fontWeight: 600, textAlign: "left" },
                ]}
              >
                Product Name
              </Text>
            </View>

            <View style={[styles.tableCol, styles.tableHead]}>
              <Text
                style={[
                  styles.tableCell,
                  { fontWeight: 600, textAlign: "left" },
                ]}
              >
                Product Code
              </Text>
            </View>

            <View style={[styles.tableCol, styles.tableHead]}>
              <Text
                style={[
                  styles.tableCell,
                  { fontWeight: 600, textAlign: "left" },
                ]}
              >
                Unit
              </Text>
            </View>

            <View style={[styles.tableCol, styles.tableHead]}>
              <Text
                style={[
                  styles.tableCell,
                  { fontWeight: 600, textAlign: "left" },
                ]}
              >
                QTY
              </Text>
            </View>

            <View style={[styles.tableCol, styles.tableHead]}>
              <Text
                style={[
                  styles.tableCell,
                  { fontWeight: 600, textAlign: "left" },
                ]}
              >
                Weighing
              </Text>
            </View>

            <View style={[styles.tableCol, styles.tableHead]}>
              <Text
                style={[
                  styles.tableCell,
                  { fontWeight: 600, textAlign: "left" },
                ]}
              >
                Moisture
              </Text>
            </View>

            <View style={[styles.tableCol, styles.tableHead]}>
              <Text
                style={[
                  styles.tableCell,
                  { fontWeight: 600, textAlign: "left" },
                ]}
              >
                N.W.
              </Text>
            </View>

            {status === "Approved" && (
              <View style={[styles.tableCol, styles.tableHead]}>
                <Text
                  style={[
                    styles.tableCell,
                    { fontWeight: 600, textAlign: "left" },
                  ]}
                >
                  Returned Qty
                </Text>
              </View>
            )}

            {status === "Approved" && (
              <View style={[styles.tableCol, styles.tableHead]}>
                <Text
                  style={[
                    styles.tableCell,
                    { fontWeight: 600, textAlign: "left" },
                  ]}
                >
                  T.N.W
                </Text>
              </View>
            )}

            <View style={[styles.tableCol, styles.tableHead]}>
              <Text
                style={[
                  styles.tableCell,
                  { fontWeight: 600, textAlign: "left" },
                ]}
              >
                Price
              </Text>
            </View>

            <View style={[styles.tableCol, styles.tableHead]}>
              <Text
                style={[
                  styles.tableCell,
                  { fontWeight: 600, textAlign: "left" },
                ]}
              >
                Subtotal
              </Text>
            </View>
          </View>

          {items.map((item, index) => (
            <View style={styles.tableRow} key={index}>
              {/* Warehouse */}
              <View style={{ ...styles.tableCol, borderLeftWidth: 1 }}>
                <Text style={[styles.tableCell, { textAlign: "left" }]}>
                  {warehouseName}
                </Text>
              </View>
              {/* Product Name */}
              <View style={styles.tableCol}>
                <Text style={[styles.tableCell, { textAlign: "left" }]}>
                  {item.productName}
                </Text>
              </View>
              {/* Product Code */}
              <View style={styles.tableCol}>
                <Text style={[styles.tableCell, { textAlign: "left" }]}>
                  {item.productCode}
                </Text>
              </View>
              {/* Unit */}
              <View style={styles.tableCol}>
                <Text style={[styles.tableCell, { textAlign: "left" }]}>
                  {item.unitOfMeasure}
                </Text>
              </View>
              {/* QTY */}
              <View style={styles.tableCol}>
                <Text style={[styles.tableCell, { textAlign: "left" }]}>
                  {formatToTwoDecimal(item.quantity)}
                </Text>
              </View>
              {/* Weighing */}
              <View style={styles.tableCol}>
                <Text style={[styles.tableCell, { textAlign: "left" }]}>
                  TBA
                </Text>
              </View>
              {/* Moisture */}
              <View style={styles.tableCol}>
                <Text style={[styles.tableCell, { textAlign: "left" }]}>
                  {formatToTwoDecimal(item.moisture)}%
                </Text>
              </View>
              {/* N.W */}
              <View style={styles.tableCol}>
                <Text style={[styles.tableCell, { textAlign: "left" }]}>
                  {formatToTwoDecimal(item.netWeight)}
                </Text>
              </View>

              {status === "Approved" && (
                <View style={styles.tableCol}>
                  <Text style={[styles.tableCell, { textAlign: "left" }]}>
                    {formatToTwoDecimal(
                      calculateReturnedQuantity(
                        item.staticNetWeight || 0,
                        item.quantity || 0,
                        item.moisture || 0,
                      ),
                    )}
                  </Text>
                </View>
              )}

              {status === "Approved" && (
                <View style={styles.tableCol}>
                  <Text style={[styles.tableCell, { textAlign: "left" }]}>
                    {formatToTwoDecimal(item.netWeight)}
                  </Text>
                </View>
              )}
              {/* Price */}
              <View style={styles.tableCol}>
                <Text style={[styles.tableCell, { textAlign: "left" }]}>
                  <Text style={styles.currencySymbol}>{currencySymbol}</Text>
                  {formatToTwoDecimal(item.unitPrice)}
                </Text>
              </View>
              {/* Subtotal */}
              <View style={styles.tableCol}>
                <Text style={[styles.tableCell, { textAlign: "left" }]}>
                  {formatToTwoDecimal(item.subtotal)}
                </Text>
              </View>
            </View>
          ))}

          {/* Totals row */}
          <View style={styles.tableRow}>
            <View
              style={[
                styles.tableCol,
                styles.tableHead,
                { borderLeftWidth: 1, width: "33.33332%" },
              ]}
            >
              <Text style={[styles.totalTableCell, { fontWeight: 600 }]}>
                Total:
              </Text>
            </View>

            <View style={[styles.tableCol, styles.tableHead]}>
              <Text
                style={[
                  styles.tableCell,
                  { fontWeight: 600, textAlign: "left" },
                ]}
              >
                {formatToTwoDecimal(totalQuantity)}
              </Text>
            </View>

            <View style={[styles.tableCol, styles.tableHead]}>
              <Text
                style={[
                  styles.tableCell,
                  { fontWeight: 600, textAlign: "left" },
                ]}
              >
                TBA
              </Text>
            </View>

            <View style={[styles.tableCol, styles.tableHead]}>
              <Text
                style={[
                  styles.tableCell,
                  { fontWeight: 600, textAlign: "left" },
                ]}
              >
                {formatToTwoDecimal(getTotalMoisture(items))}
              </Text>
            </View>

            <View style={[styles.tableCol, styles.tableHead]}>
              <Text
                style={[
                  styles.tableCell,
                  { fontWeight: 600, textAlign: "left" },
                ]}
              >
                {formatToTwoDecimal(totalNetWeight)}
              </Text>
            </View>

            {status === "Approved" && (
              <View style={[styles.tableCol, styles.tableHead]}>
                <Text
                  style={[
                    styles.tableCell,
                    { fontWeight: 600, textAlign: "left" },
                  ]}
                >
                  {formatToTwoDecimal(
                    items.reduce((total, item) => {
                      const returned = calculateReturnedQuantity(
                        item.staticNetWeight || 0,
                        item.quantity || 0,
                        item.moisture || 0,
                      );
                      return total + returned;
                    }, 0),
                  )}
                </Text>
              </View>
            )}

            {status === "Approved" && (
              <View style={[styles.tableCol, styles.tableHead]}>
                <Text
                  style={[
                    styles.tableCell,
                    { fontWeight: 600, textAlign: "left" },
                  ]}
                >
                  {formatToTwoDecimal(totalNetWeight)}
                </Text>
              </View>
            )}

            <View style={[styles.tableCol, styles.tableHead]}>
              <Text
                style={[
                  styles.tableCell,
                  { fontWeight: 600, textAlign: "left" },
                ]}
              ></Text>
            </View>

            <View style={[styles.tableCol, styles.tableHead]}>
              <Text
                style={[
                  styles.tableCell,
                  { fontWeight: 600, textAlign: "left" },
                ]}
              >
                <Text style={styles.currencySymbol}>{currencySymbol} </Text>
                {formatToTwoDecimal(subTotal)}
              </Text>
            </View>
          </View>
        </View>

        {/* ── BOTTOM SECTION: left card + right summary ── */}
        <View
          style={{ ...styles.totalContainer, justifyContent: "space-between" }}
        >
          {/* ── LEFT CARD ── */}
          <View style={styles.totalCard}>
            <View style={styles.summaryColumn}>
              <Text style={styles.label}>Container Number: </Text>
              <Text style={styles.summaryValue}>
                {containerNumber || "TBA"}
              </Text>
            </View>
            <View style={styles.summaryColumn}>
              <Text style={styles.label}>Pier: </Text>
              <Text style={styles.summaryValue}>{pier || "TBA"}</Text>
            </View>
            <View style={styles.summaryColumn}>
              <Text style={styles.label}>Shipping Fee: </Text>
              <Text style={styles.summaryValue}>
                <Text style={styles.currencySymbol}>{currencySymbol} </Text>
                {formatToTwoDecimal(shipping_fee) || "TBA"}
              </Text>
            </View>

            {/* Payment Method, Due Date, Payment Terms, Destination moved here */}
            <View style={styles.summaryColumn}>
              <Text style={styles.label}>Payment Method: </Text>
              <Text style={styles.summaryValue}>{selectedMethod}</Text>
            </View>
            <View style={styles.summaryColumn}>
              <Text style={styles.label}>Due Date: </Text>
              <Text style={styles.summaryValue}>
                {selectedDueDate || "TBA"}
              </Text>
            </View>
            <View style={styles.summaryColumn}>
              <Text style={styles.label}>Payment Terms: </Text>
              <Text style={styles.summaryValue}>
                {inputPaymentTerms || "TBA"}
              </Text>
            </View>
            <View style={styles.summaryColumn}>
              <Text style={styles.label}>Destination: </Text>
              <Text style={styles.summaryValue}>{selectedDestination}</Text>
            </View>
          </View>

          {/* ── RIGHT SUMMARY ── */}
          <View
            style={{
              gap: "0.5rem",
              flexDirection: "column",
              alignItems: "flex-end",
              padding: "10px",
              marginLeft: "auto",
              size: "7",
            }}
          >
            {renderSummaryBlock(isReOpen)}
          </View>
        </View>

        <View style={styles.footer}>
          <Text style={styles.footerText}>{transactionId}</Text>
          <Text style={styles.footerText}>{today}</Text>
        </View>
      </Page>
    </Document>
  );
};

export default InvoicePDF;
